import { API } from "@infra/api";
import { logger } from "@infra/logger";
import { toBoundaryGeoJSON, toSHPFile } from "@domain/Geo/io";
import type { IJobCreation } from "@ports/jobs/IJobCreation";
import type { IJobs } from "@ports/jobs/IJobs";
import type { IJobIssues } from "@ports/jobs/IJobIssues";
import type { Branch } from "@domain/CRM/branch";
import type { Contact } from "@domain/CRM/contact";
import type { Deal } from "@domain/CRM/deal";
import type { User } from "@domain/Auth";
import type { Grower } from "@domain/CRM/grower";
import type { Region } from "@domain/CRM/region";
import type { Job } from "@domain/CRM/job";
import {
	makeAgrianIssuesUseCases,
	makeJobErrorsUseCases,
	makeJobsDashboardUseCases,
	makeJobsDetailUseCases,
	makeJobsListUseCases,
	makeMarkJobsReadyUseCases,
	makeSubmitJobsUseCases,
} from "@app/jobs";

const jobCreationApi: IJobCreation = {
	getGrowersByFilter: (filter) => API.Data.Growers.get.byFilter(filter) as Promise<Grower[]>,
	createGrowers: (growers) => API.Data.Growers.create.many(growers) as Promise<Grower[]>,
	createContacts: (contacts) => API.Data.Contacts.create.many(contacts) as Promise<Contact[]>,
	getContactsByIds: (ids) => API.Data.Contacts.get.byIds(ids) as Promise<Contact[]>,
	getBranchesByIds: (ids) => API.Data.Branches.get.byIds(ids) as Promise<Branch[]>,
	updateContacts: (contacts) => API.Data.Contacts.update.many(contacts) as unknown as Promise<void>,
	updateBranches: (branches) => API.Data.Branches.update.many(branches) as unknown as Promise<void>,
};

const jobsApi: IJobs = {
	getById: (id, compressed) => API.Data.Jobs.get.byId(id, compressed) as Promise<Job | undefined>,
	getByCompany: (company, branchNames) => API.Data.Jobs.get.byCompany(company, branchNames) as Promise<Job[]>,
	getByCompanyStream: (company, branchNames, onBatch) =>
		API.Data.Jobs.get.byCompanyStream(company, branchNames, onBatch) as Promise<Job[]>,
	getUnscheduled: () => API.Data.Jobs.get.unscheduled(),
	getAttachmentsByIds: (ids) => API.Data.Jobs.get.attachmentsByIds(ids),
	getLastCropOptions: (fresh) => API.Data.Jobs.get.lastCropOptions(fresh),
	getNextCropOptions: (fresh) => API.Data.Jobs.get.nextCropOptions(fresh),
	updateOne: (job) => API.Data.Jobs.update.one(job as Job) as Promise<Job>,
	updateMany: (jobs, compressed) => API.Data.Jobs.update.many(jobs, compressed) as unknown as Promise<void>,
	updateField: (id, field, value) => API.Data.Jobs.update.field(id, field, value) as Promise<Job>,
	setOrders: (updates) => API.Data.Jobs.update.orders(updates),
	setDropoffs: (updates) => API.Data.Jobs.update.dropoffs(updates),
	setLocations: (updates) => API.Data.Jobs.update.locations(updates),
	applyMapFeatureEdits: (edits) => API.Data.Jobs.update.mapFeatures(edits),
	uploadFile: (id, file) => API.Data.Jobs.update.files(id, [file]).then((r) => r[0]),
};

const jobIssuesApi: IJobIssues = {
	getAgrian: () => API.Data.Jobs.issues.getAgrian(),
	fixAgrian: (ids) => API.Data.Jobs.issues.fixAgrian(ids),
	getBranchMatcherJobs: () => API.Data.Jobs.issues.getBranchMatcherJobs(),
	getDealMatcherJobs: () => API.Data.Jobs.issues.getDealMatcherJobs(),
	fixDealMatcherJobs: (ids) => API.Data.Jobs.issues.fixDealMatcherJobs(ids),
	fixExeMissionValidationJobs: (ids) => API.Data.Jobs.issues.fixExeMissionValidationJobs(ids),
};

/**
 * Bound jobs use cases. Each sub-namespace is the output of one narrow
 * factory; consumers go through the single `useJobUseCases()` hook and
 * descend into the relevant sub-namespace (`jobs.list.loadByFilter`,
 * `jobs.markReady.loadGrowersWithUnreadyJobs`, etc.).
 *
 * The aggregation lives at the composition layer, not the Application layer,
 * because the deps required for each sub-feature are different and only the
 * composition root knows how to wire them. The Application factories stay
 * narrow so unit tests can construct one sub-namespace at a time.
 */
export const jobUseCases = {
	errors: makeJobErrorsUseCases({ processGeo: (id) => API.Data.Jobs.issues.processGeo(id) }),
	list: makeJobsListUseCases({ jobsApi }),
	dashboard: makeJobsDashboardUseCases({
		getBranchesByCompany: (company, fresh) => API.Data.Branches.get.byCompany(company, fresh) as Promise<Branch[]>,
		getRegionsByCompany: (company, fresh) =>
			API.Data.Regions.get.byCompany(company, fresh) as unknown as Promise<Region[]>,
		getDealsByCompany: (company, fresh) => API.Data.Deals.get.byCompany(company, true, fresh) as Promise<Deal[]>,
	}),
	detail: makeJobsDetailUseCases({ jobsApi, toSHP: toSHPFile, toBoundaryGeoJSON }),
	agrianIssues: makeAgrianIssuesUseCases({ jobIssuesApi }),
	markReady: makeMarkJobsReadyUseCases({
		updateJobs: (jobs, compressed) => API.Data.Jobs.update.many(jobs, compressed) as unknown as Promise<void>,
		getGrowersWithUnreadyJobs: (company, fresh) =>
			API.Data.Growers.get.withUnreadyJobs(company, fresh) as Promise<Grower[]>,
		getJobsForGrowers: (growers) => API.Data.Jobs.get.byGrowers(growers) as Promise<Job[]>,
		logger,
	}),
	submit: makeSubmitJobsUseCases({
		jobCreationApi,
		getCurrentUser: () => API.Users.me() as Promise<User>,
		getDealsByIds: (ids) => API.Data.Deals.get.byIds(ids) as Promise<Deal[]>,
		createJobs: (jobs) => API.Data.Jobs.create.many(jobs) as Promise<Job[]>,
		logger,
		getCompanyById: (id) => API.Data.Companies.get.byId(id),
		getBranchesByCompany: (company, fresh) => API.Data.Branches.get.byCompany(company, fresh) as Promise<Branch[]>,
		getRegionsByCompany: (company, fresh) =>
			API.Data.Regions.get.byCompany(company, fresh) as unknown as Promise<Region[]>,
		getGrowersByCompany: (company, fresh) => API.Data.Growers.get.byCompany(company, fresh) as Promise<Grower[]>,
		getDealsByCompany: (company, fresh) => API.Data.Deals.get.byCompany(company, false, fresh) as Promise<Deal[]>,
		getContactsByCompany: (company, fresh) => API.Data.Contacts.get.byCompany(company, fresh) as Promise<Contact[]>,
		getLastCropOptions: (fresh) => API.Data.Jobs.get.lastCropOptions(fresh),
		getNextCropOptions: (fresh) => API.Data.Jobs.get.nextCropOptions(fresh),
		updateGrowerPhoneNumber: (growerId, phoneNumber) => API.Data.Growers.update.phoneNumber(growerId, phoneNumber),
	}),
};

export type JobUseCases = typeof jobUseCases;
